import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider } from "@mui/material"
import IconButton from "@mui/material/IconButton"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { Controller, useForm } from "react-hook-form"

import { AutoComplete } from "@/components/common/Autocomplete"
import { Button } from "@/components/common/Button"
import { useAccountManagers } from "@/hooks/api/useAccountManagers"
import useHandleCloseWithDataSave from "@/screens/Companies/edit/components/locations/hooks/useHandleCloseWithDataSave"
import {
  type UpdateAccountManagerDialogProps,
  type UpdateAccountManagerProps,
  UpdateAccountManagerSchema,
} from "@/screens/Companies/types/dialogProps"

interface Option {
  id: string
  name: string
}
const defaultValue: Option = { id: "", name: "" }

export const EditAccountManagerDialog: FC<UpdateAccountManagerDialogProps> = ({
  activeEditLocation,
  onClose,
  onSubmit,
}) => {
  const accountManager = activeEditLocation?.accountManager
    ? ({
        id: activeEditLocation?.accountManager?.contactId,
        name: `${activeEditLocation?.accountManager?.firstName} ${activeEditLocation?.accountManager?.lastName}`,
      } as Option)
    : defaultValue

  const methods = useForm<{ accountManager: Option }>({
    values: {
      accountManager,
    },
    resolver: zodResolver(UpdateAccountManagerSchema),
    mode: "onBlur",
  })

  const { visibleManagers } = useAccountManagers({})

  const managers = visibleManagers.map(({ name, userId: id }) => ({
    name,
    id,
  }))

  const handleSave = async (value: UpdateAccountManagerProps) => {
    const isValid = await methods.trigger()
    if (isValid) {
      onSubmit(value)
      onClose({ data: { ...value }, reason: "success" })
    }
  }

  const handleClose = useHandleCloseWithDataSave(methods.getValues, onClose)

  return (
    <Dialog open={true} onClose={handleClose} fullWidth>
      <DialogTitle className='pl-10'>
        <span className='font-normal text-primary'>Update Account Manager</span>
      </DialogTitle>
      <IconButton aria-label='close' onClick={handleClose} className='absolute right-4 top-4 text-primary'>
        <CloseOutlined />
      </IconButton>
      <Divider />
      <DialogContent classes={{ root: "p-10" }}>
        <form>
          <Controller
            name='accountManager'
            control={methods.control}
            render={() => (
              <Box data-testid='location-address-details-form-postal-code'>
                <AutoComplete<Option>
                  label='Account Manager'
                  placeholder='Select Account Manager'
                  name='accountManager'
                  id='accountManager'
                  options={managers}
                  getOptionLabel={({ name }) => name}
                  control={methods.control}
                  clearOnEscape={true}
                  defaultEmptyState={defaultValue}
                />
              </Box>
            )}
          />
        </form>
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button variant='primary' appearance='outlined' onClick={handleClose}>
          Cancel
        </Button>
        <Button variant='primary' onClick={methods.handleSubmit(handleSave)}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
